import Page from '../common/page'
import bus from '~/bus.js'
import Api from '../api.js'
import store from '~/store/store'
import {useStream} from '~/commons/stream'
import { v4 as uuidv4 } from 'uuid'
import router from '~/router'
import useLanguageTree from '~/commons/language'

const { getStreamEventName } = useStream()

const {t} = useLanguageTree()

const state = Object.assign({}, Page.state, {
  endpoint: 'file/full-search/',
  searchInput: '',
  searchFields: {},
  sortOptions: {},
  offsets: []
})

export default {
  namespaced: true,
  state,
  getters: {
    ...Page.getters,
    isSearchApplied: state => {
      return (state.searchFields && Object.keys(state.searchFields).filter(x => state.searchFields[x] !== null && state.searchFields !== '').length > 0) || state.searchInput !== ''
    },
    searchNumber: state => {
      return Object.keys(state.searchFields).filter(x => state.searchFields[x] !== null && state.searchFields !== '').length
    },
    formattedFilters: state => {
      if (state.searchFields && Object.keys(state.searchFields).filter(x => state.searchFields[x] !== null && state.searchFields !== '').length > 0) {
        const dateFilter = {}
        if (state.searchFields.date) {
          if (state.searchFields.date.start) {
            const startDate = new Date(state.searchFields.date.start)
            startDate.setUTCHours(0, 0, 0, 0)
            dateFilter.From = startDate.toISOString()
          }
          if (state.searchFields.date.end) {
            const endDate = new Date(state.searchFields.date.end)
            endDate.setUTCHours(23, 59, 59, 999)
            dateFilter.To = endDate.toISOString()
          }
        }

        return {
          name: state.searchFields.name,
          exact_match: state.searchFields.exactMatch,
          type: state.searchFields.type,
          is_informed: state.searchFields.isInformed,
          is_starred: state.searchFields.isStarred,
          description: state.searchFields.description,
          parent_id: state.searchFields.inTrash ? 'trash' : undefined,
          created_at: (state.searchFields.date && state.searchFields.date.type === 'create') ? dateFilter : undefined,
          updated_at: (state.searchFields.date && state.searchFields.date.type === 'update') ? dateFilter : undefined,
          colors: state.searchFields.colors,
          vault: state.searchFields.vault ? state.searchFields.vault.address : undefined
        }
      }
      return {}
    },
    getFilterField: state => key => {
      if (state.searchFields[key]) {
        return state.searchFields[key]
      }
      return null
    },
    isInSearchAndFilters: state => (action, newValue) => {
      if (router.app.$route.name === 'mysearch') {
        const filtersAffected = searchFiltersAffected(state, action, newValue)
        if (filtersAffected) {
          store.dispatch('feedback/addSnack', { text: t('file not visible in search'), type: 'error', timeout: 5000 })
        }
        return filtersAffected
      }
      return false
    },
    hasSpecialCharacters: () => (str) => {
      return !/^[a-z0-9 \-_.]+$/i.test(str)
    },
    getSearchAmongGroups: state => {
      return state.searchFields.searchAmongGroups
    },
    checkSearchAmongGroups: state => {
      if (state.searchFields.searchAmongGroups) {
        if ((!state.searchFields.name || state.searchFields.name.trim() === '') && (state.searchInput.trim() === '')) {
          return false
        }
      }
      return true
    }
  },
  mutations: {
    ...Page.mutations,
    setSearchFields (state, fields) {
      state.searchFields = {}
      Object.keys(fields).forEach(key => {
        state.searchFields = { ...state.searchFields, [key]: fields[key] }
      })
    },
    setSearchField (state, {key, value}) {
      state.searchFields = { ...state.searchFields, [key]: value }
    },
    setColorSearchField (state, colors) {
      state.searchFields = { ...state.searchFields, colors }
    },
    setSearchInput (state, input) {
      state.searchInput = input
    },
    clearSearch (state) {
      state.searchInput = ''
      state.searchFields = {}
      state.offsets = []
    },
    clearOffsets (state) {
      state.offsets = []
    }
  },
  actions: {
    ...Page.actions,
    async loadElements ({commit, state, getters, dispatch}) {
      let endpoint = state.endpoint
      dispatch('filters/clearRouteFilters', '/mysearch', {root: true})
      let freeText
      if (state.searchInput !== '') {
        if (getters.hasSpecialCharacters(state.searchInput)) {
          freeText = state.searchInput
        } else {
          endpoint += state.searchInput + '/'
        }
      }
      endpoint += `?count=${state.resultsPerPage}&page=${state.page}`

      const eventID = uuidv4()

      let results = []
      let rs
      bus.$on(getStreamEventName(eventID), (response) => {
        response = response || { results: [] }
        state.offsets = response.offsets
        results.push(...(response.results || []))
        if (response.paginator) {
          commit('lastLoadedPage', response.paginator.current_page)
        }
        commit('loadElements', results || [])
        commit('totalElementsCount',
          typeof response.paginator === 'undefined'
            ? 0
            : response.paginator.result_total_count)
        bus.$emit('pageElementsLoaded', {
          elements: results || [],
          endpoint: state.endpoint,
          page: response.paginator ? response.paginator.current_page : 1
        })
        bus.$emit(state.endpoint + 'loadedElements', results || [])
        rs = response
        return response
      })
      try {
        await Api.fetchStream(endpoint, {
          fetch: { method: 'POST' },
          data: JSON.stringify({
            actor_target_id: 'account/' + store.getters['session/accountUUID'],
            filters: getters.formattedFilters,
            search_among_groups: getters.getSearchAmongGroups,
            free_text: freeText,
            selected_fields: [
              'fileID',
              'parentID',
              'oldFileID',
              'name',
              'description',
              'type',
              'platformTags',
              'countChildren',
              'data',
              'createdAt',
              'isInformed',
              'isShared',
              'acl',
              'isStarred',
              'isDeleted',
              'isReport ',
              'isDisabled ',
              'updatedAt  ',
              'deletedAt  ',
              'actorOwnerID',
              'namedPath'
            ],
            step_offsets: state.offsets
          }),
          eventID
        })
      } catch (error) {
        store.dispatch('feedback/addSnack', { text: error, type: 'error' })
      } finally {
        bus.$off(getStreamEventName(eventID))
      }
      return rs
    },
    setSearchFields ({commit}, payload) {
      commit('setSearchFields', payload)
    },
    setFilterField ({commit}, payload) {
      if (!payload || typeof payload !== 'object' || !payload.key || !Object.hasOwn(payload, 'value')) {
        console.error('Invalid payload', payload)
        return
      }
      commit('setSearchField', payload)
    },
    setColorSearchField ({commit}, payload) {
      commit('setColorSearchField', payload)
    },
    clearSearch ({commit}) {
      commit('clearSearch')
    },
    clearOffsets ({commit}) {
      commit('clearOffsets')
    },
    setSearchInput ({commit}, input) {
      commit('setSearchInput', input)
    }
  }
}

function searchFiltersAffected (state, action, newValue) {
  if (state.searchFields.date && state.searchFields.date.type === 'update') {
    if (state.searchFields.date.end) {
      const end = new Date(state.searchFields.date.end).setUTCHours(0, 0, 0, 0)
      const today = new Date().setUTCHours(0, 0, 0, 0)
      if (end < today) {
        return true
      }
    }
  }
  switch (action) {
    case 'move':
      // FUTURE: where the source search filter exists, check the new parent id
      return false
    case 'rename':
      if (state.searchInput === '' && !state.searchFields.name) {
        return false
      } else {
        const searchInput = state.searchInput
        if (!!searchInput && newValue.match(searchInput)) {
          return false
        }
        const fieldName = state.searchFields.name
        const exactMatch = !!state.searchFields.exactMatch
        if (!!fieldName && exactMatch && newValue === fieldName) {
          return false
        } else if (!!fieldName && newValue.match(fieldName)) {
          return false
        }
        return true
      }
    case 'favourite':
      if (state.searchFields.isStarred !== null && state.searchFields.isStarred !== undefined) {
        return state.searchFields.isStarred !== newValue
      }
      return false
    case 'inform':
      if (state.searchFields.isInformed !== null && state.searchFields.isInformed !== undefined) {
        return state.searchFields.isInformed !== newValue
      }
      return false
    case 'changeColor':
      if (state.searchFields.colors) {
        return !state.searchFields.colors.includes(newValue)
      }
      return false
    default:
      return false
  }
}



// WEBPACK FOOTER //
// ./src/store/idonia-api/search/search.js